import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { CourseCard } from "../components/CourseCard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { BookOpen, Clock, CheckCircle2, XCircle, GraduationCap } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    api.get("/me/enrollments").then((res) => setEnrollments(res.data));
    api.get("/me/payments").then((res) => setPayments(res.data));
  }, []);

  const statusBadge = (s) => {
    if (s === "approved") return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Approved</span>;
    if (s === "rejected") return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-100 text-rose-700"><XCircle className="h-3.5 w-3.5" /> Rejected</span>;
    return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700"><Clock className="h-3.5 w-3.5" /> Pending</span>;
  };

  return (
    <div className="min-h-screen bg-[#FAFAFC]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Hi, {user?.name} 👋</h1>
        <p className="text-slate-500 mt-1">Continue where you left off.</p>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-8">
          {[
            { icon: BookOpen, label: "Enrolled Courses", value: enrollments.length, color: "text-teal-700 bg-teal-50" },
            { icon: Clock, label: "Pending Payments", value: payments.filter((p) => p.status === "pending").length, color: "text-amber-600 bg-amber-50" },
            { icon: GraduationCap, label: "Total Purchases", value: payments.filter((p) => p.status === "approved").length, color: "text-emerald-600 bg-emerald-50" },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl bg-white border border-slate-200 p-5">
              <span className={`grid place-items-center h-10 w-10 rounded-xl ${s.color} mb-3`}><s.icon className="h-5 w-5" /></span>
              <p className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{s.value}</p>
              <p className="text-sm text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="courses" className="mt-10">
          <TabsList>
            <TabsTrigger value="courses" data-testid="dashboard-tab-courses">My Courses</TabsTrigger>
            <TabsTrigger value="payments" data-testid="dashboard-tab-payments">Payment History</TabsTrigger>
          </TabsList>

          <TabsContent value="courses" className="mt-6">
            {enrollments.length === 0 ? (
              <div className="text-center py-16 rounded-2xl bg-white border border-slate-200">
                <p className="text-slate-500">You haven't enrolled in any course yet.</p>
                <Link to="/courses" className="text-teal-700 font-semibold text-sm mt-2 inline-block">Browse courses →</Link>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {enrollments.map((c) => <CourseCard key={c.id} course={{ ...c, module_count: c.module_count || 0 }} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="payments" className="mt-6">
            {payments.length === 0 ? (
              <p className="text-center text-slate-500 py-16 rounded-2xl bg-white border border-slate-200">No payments yet.</p>
            ) : (
              <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-left">
                    <tr>
                      <th className="px-5 py-3 font-medium">Course</th>
                      <th className="px-5 py-3 font-medium">Amount</th>
                      <th className="px-5 py-3 font-medium">UTR</th>
                      <th className="px-5 py-3 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {payments.map((p) => (
                      <tr key={p.id} data-testid={`payment-row-${p.id}`}>
                        <td className="px-5 py-3.5 font-medium text-slate-800">{p.course_title}</td>
                        <td className="px-5 py-3.5">₹{p.amount}</td>
                        <td className="px-5 py-3.5 font-mono text-xs text-slate-500">{p.utr}</td>
                        <td className="px-5 py-3.5">
                          {statusBadge(p.status)}
                          {p.status === "rejected" && p.rejection_reason && <p className="text-xs text-rose-500 mt-1">{p.rejection_reason}</p>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
